import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClockCounterClockwise, CaretRight, Package, Motorcycle, ForkKnife, XCircle } from '@phosphor-icons/react';
import { getMyOrders } from '../../services/customerApi';
import { useCustomer, PageWrap } from './CustomerApp';
import { toast } from 'sonner';

const statusColors = {
  NEW: 'bg-blue-100 text-blue-700',
  PREPARING: 'bg-[#F57C00]/10 text-[#F57C00]',
  READY: 'bg-[#E21836]/10 text-[#E21836]',
  READY_DINEIN: 'bg-[#E21836]/10 text-[#E21836]',
  READY_TAKEAWAY: 'bg-[#E21836]/10 text-[#E21836]',
  READY_DELIVERY: 'bg-[#E21836]/10 text-[#E21836]',
  RIDER_ASSIGNED: 'bg-purple-100 text-purple-700',
  OUT_FOR_DELIVERY: 'bg-[#E21836]/10 text-[#E21836]',
  DELIVERED: 'bg-[#2E7D32]/10 text-[#2E7D32]',
  SERVED: 'bg-[#2E7D32]/10 text-[#2E7D32]',
  PICKED_UP: 'bg-[#2E7D32]/10 text-[#2E7D32]',
  CANCELLED: 'bg-[#D32F2F]/10 text-[#D32F2F]',
  COMPLETE: 'bg-gray-100 text-gray-600',
};

const typeIcons = { DELIVERY: Motorcycle, TAKEAWAY: Package, DINE_IN: ForkKnife };

export default function CustomerOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const res = await getMyOrders();
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <PageWrap>
      <div className="min-h-screen pb-24" data-testid="orders-page">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#FFFDF9]/80 backdrop-blur-xl border-b border-[#E8E2D6]/50 px-4 py-3">
          <h2 className="text-lg font-bold text-[#2D231E]">My Orders</h2>
        </div>

        <div className="px-4 pt-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-3 border-[#E21836] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center py-16">
              <ClockCounterClockwise size={48} weight="duotone" className="text-[#E8E2D6] mb-3" />
              <h3 className="text-lg font-bold text-[#2D231E] mb-1">No orders yet</h3>
              <p className="text-sm text-[#6C5D53] mb-4">Place your first order to see it here</p>
              <button
                data-testid="start-ordering-btn"
                onClick={() => navigate('/order/menu')}
                className="px-5 py-2 bg-[#E21836] text-white text-sm font-semibold rounded-full"
              >
                Start Ordering
              </button>
            </div>
          ) : (
            orders.map((order, i) => {
              const TypeIcon = typeIcons[order.order_type] || Package;
              const statusClass = statusColors[order.status] || 'bg-gray-100 text-gray-600';
              return (
                <motion.button
                  key={order.id}
                  data-testid={`order-card-${order.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/order/track/${order.id}`)}
                  className="w-full text-left p-4 bg-[#F6F3EC] border border-[#E8E2D6] rounded-2xl hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TypeIcon size={16} weight="duotone" className="text-[#E21836]" />
                      <span className="font-bold text-sm text-[#2D231E]">#{order.order_number}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusClass}`}>
                        {order.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <CaretRight size={14} className="text-[#6C5D53]" />
                  </div>
                  <p className="text-xs text-[#6C5D53] mb-1">
                    {(order.items || []).map((item) => `${item.menu_item_name} x${item.qty}`).join(', ')}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#6C5D53]">{formatDate(order.created_at)}</span>
                    <span className="text-sm font-bold text-[#2D231E]">&#8377;{(order.total || 0).toFixed(0)}</span>
                  </div>
                </motion.button>
              );
            })
          )}
        </div>
      </div>
    </PageWrap>
  );
}
